import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api } from "../../lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { CurrencyInr, TrendUp, Warning, Receipt } from "@phosphor-icons/react";

const CHART_COLORS = ["#2D5A27", "#C25D38", "#D4A418", "#6E8A4A"];

export default function Revenue() {
  const [rev, setRev] = useState(null);
  const [dues, setDues] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/finance/revenue"), api.get("/finance/dues")])
      .then(([r, d]) => { setRev(r.data); setDues(d.data); });
  }, []);

  if (!rev) return <AppShell title="Revenue" subtitle="Finance"><div className="p-6">Loading…</div></AppShell>;

  return (
    <AppShell title="Revenue Dashboard" subtitle="Module 12 · Finance">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="revenue-kpis">
        <Kpi label="Total paid" value={`₹${rev.total_paid.toFixed(2)}`} icon={CurrencyInr} accent/>
        <Kpi label="Invoiced" value={`₹${rev.total_invoiced.toFixed(2)}`} icon={Receipt}/>
        <Kpi label="Outstanding" value={`₹${rev.outstanding.toFixed(2)}`} icon={Warning} danger/>
        <Kpi label="Txns · Invoices" value={`${rev.paid_count} / ${rev.invoice_count}`} icon={TrendUp}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E2D9] rounded-md p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Daily revenue</div>
          <div className="font-heading text-lg mb-4">Paid transactions by date</div>
          <div className="h-72 min-h-[288px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rev.by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9"/>
                <XAxis dataKey="date" stroke="#5C5A55" fontSize={11}/>
                <YAxis stroke="#5C5A55" fontSize={11}/>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E2D9", borderRadius: 6, fontSize: 12 }}/>
                <Line type="monotone" dataKey="amount" stroke="#2D5A27" strokeWidth={2} dot={{ r: 4, fill: "#2D5A27" }}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
          {rev.by_day.length === 0 && <div className="text-xs text-[#8A8780] mt-3">No paid transactions yet.</div>}
        </div>

        <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Provider split</div>
          <div className="font-heading text-lg mb-4">Revenue by provider</div>
          <div className="h-56 min-h-[224px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rev.by_provider.length ? rev.by_provider : [{ provider: "no data", amount: 1 }]}
                     innerRadius={40} outerRadius={75} dataKey="amount" nameKey="provider">
                  {(rev.by_provider.length ? rev.by_provider : [{}]).map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}/>
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E2D9", borderRadius: 6, fontSize: 12 }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {rev.by_provider.map((p, i) => (
              <div key={p.provider} className="text-xs text-[#5C5A55] flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}/>
                {p.provider}: ₹{p.amount.toFixed(0)}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-2">
          <Warning size={16} color="#C25D38" weight="fill"/>
          <div className="font-heading">Customer dues</div>
          <div className="flex-1"/>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">{dues.length} customers</div>
        </div>
        <table className="w-full text-sm" data-testid="dues-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Customer</th>
              <th className="px-6 py-3 text-right">Outstanding</th>
              <th className="px-6 py-3">Invoices</th>
              <th className="px-6 py-3">Last</th>
            </tr>
          </thead>
          <tbody>
            {dues.length === 0 && <tr><td colSpan={4} className="px-6 py-10 text-center text-[#8A8780]">No outstanding dues.</td></tr>}
            {dues.map(d => (
              <tr key={d.customer_id || d.customer_name}>
                <td className="px-6 py-3 font-medium">{d.customer_name}</td>
                <td className="px-6 py-3 font-mono text-right text-[#C25D38]">₹{d.outstanding.toFixed(2)}</td>
                <td className="px-6 py-3">{d.invoices}</td>
                <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">{d.last_date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}

const Kpi = ({ label, value, icon: Icon, accent, danger }) => (
  <div className="stat-card">
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded flex items-center justify-center ${danger ? "bg-[#C25D38]/10" : accent ? "bg-[#2D5A27]/10" : "bg-[#F7F6F2]"}`}>
        <Icon size={16} color={danger ? "#C25D38" : "#2D5A27"} weight="duotone"/>
      </div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">{label}</div>
    </div>
    <div className="font-heading text-2xl mt-4">{value}</div>
  </div>
);
